<div class="reassign-container" [formGroup]="formGroup">
  <!-- Top Bar: Search and Selection Count -->
  <div class="reassign-top-bar">
    <div class="reassign-search-wrapper">
      <i class="ki-filled ki-magnifier search-icon"></i>
      <input
        placeholder="Search..."
        #searchInput
        (input)="onSearch(searchInput.value)"
        type="text"
        class="reassign-search-input"
      />
      <button
        #searchTerm
        *ngIf="searchInput.value"
        type="button"
        class="reassign-clear-btn"
        (click)="clearSearch(searchInput)"
      >
        ×
      </button>
    </div>

    <!-- Error/Nothing to submit warning -->
    <div *ngIf="showNothingToSubmit" class="reassign-warning-msg">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="2"
        stroke="currentColor"
        class="w-4 h-4"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
        />
      </svg>
      No tasks are selected for bulk assignment
    </div>

    <!-- Selection count badge -->
    <div *ngIf="selectedTasksCount > 0" class="reassign-selection-badge">
      <span class="selection-count-number">{{ selectedTasksCount }}</span>
      <span>
        {{
          fields?.fieldName === "tasks"
            ? selectedTasksCount > 1
              ? "Tasks"
              : "Task"
            : selectedTasksCount > 1
              ? "Action Items"
              : "Action Item"
        }}
        selected for reassignment
      </span>
    </div>
  </div>

  <!-- Table Header -->
  <div *ngIf="filteredTasks.length > 0" class="reassign-header">
    <ng-container
      *ngFor="let option of fields?.options?.staticOptions; let i = index"
    >
      <div
        [ngClass]="{
          'col-span-5': i === 0,
          'col-span-4': i === 1,
          'col-span-3': i === 2,
        }"
        class="flex items-center gap-2"
      >
        <!-- Select All Checkbox -->
        <input
          *ngIf="i === 0"
          type="checkbox"
          class="checkbox cursor-pointer border-slate-300 dark:border-coal-500 rounded bg-transparent"
          [checked]="isAllSelected()"
          [indeterminate]="isIndeterminate()"
          (change)="toggleSelectAll($event)"
        />
        <span>{{ option.text }}</span>
      </div>
    </ng-container>
  </div>

  <!-- Scrollable Rows Body -->
  <div class="tableBody reassign-body">
    <!-- Empty state -->
    <div *ngIf="filteredTasks.length === 0" class="reassign-empty-state">
      <div class="empty-icon-wrapper">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-8 h-8"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286Zm0 13.036h.008v.008H12v-.008Z"
          />
        </svg>
      </div>
      <div class="empty-title">No records found</div>
      <span class="empty-subtitle"
        >There are currently no records available to display</span
      >
    </div>

    <!-- Tasks List -->
    <ng-container *ngIf="fields.fieldName === 'tasks'">
      <!-- Loading Skeleton -->
      <ng-container *ngIf="isLoading; else tasksData">
        <div
          *ngFor="let item of [1, 2, 3, 4, 5]"
          class="reassign-card skeleton"
        >
          <div class="col-span-5 flex items-center gap-3">
            <div class="skeleton-checkbox"></div>
            <div class="flex flex-col gap-2 w-full">
              <div class="skeleton-text w-32"></div>
              <div class="skeleton-text w-56"></div>
            </div>
          </div>
          <div class="col-span-4 flex items-center gap-3">
            <div class="skeleton-avatar"></div>
            <div class="flex flex-col gap-2">
              <div class="skeleton-text w-28"></div>
              <div class="skeleton-text w-36"></div>
            </div>
          </div>
          <div class="col-span-3 flex items-center gap-3">
            <div class="skeleton-arrow"></div>
            <div class="skeleton-avatar"></div>
            <div class="flex flex-col gap-2">
              <div class="skeleton-text w-28"></div>
              <div class="skeleton-text w-28"></div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Real Cards -->
      <ng-template #tasksData>
        <div
          *ngFor="let task of visibleTasks; trackBy: trackByTask"
          [class.selected]="isTaskSelected(task)"
          class="reassign-card"
        >
          <!-- Checkbox + Task Details -->
          <div class="col-span-5 flex items-center gap-3">
            <input
              type="checkbox"
              class="checkbox text-blue-600 cursor-pointer"
              [id]="'option_' + task._id"
              [checked]="isTaskSelected(task)"
              (change)="toggleTaskSelection(task)"
            />
            <label [for]="'option_' + task._id" class="reassign-label">
              <span class="task-id-text" [title]="task.taskId">{{
                task.taskId
              }}</span>
              <span class="task-desc-text" [title]="task.control">{{
                task.control
              }}</span>
            </label>
          </div>

          <!-- Current Assignee -->
          <div class="col-span-4 flex items-center gap-3">
            <div
              [ngClass]="getBgColorByDepartment(task.previousAssignee?.name)"
              class="assignee-avatar"
            >
              {{ getInitials(task.previousAssignee?.name) }}
            </div>
            <div class="assignee-info">
              <span
                class="assignee-name"
                [title]="task.previousAssignee?.name || 'Unassigned'"
                >{{ task.previousAssignee?.name || "Unassigned" }}</span
              >
              <span
                class="assignee-email"
                [title]="task.previousAssignee?.email || ''"
                >{{ task.previousAssignee?.email }}</span
              >
            </div>
          </div>

          <!-- Reassign To -->
          <div class="col-span-3 flex items-center gap-3">
            <div class="reassign-arrow">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                />
              </svg>
            </div>
            <div
              [ngClass]="getBgColorByDepartment(task.currentAssignee?.name)"
              class="assignee-avatar"
            >
              {{ getInitials(task.currentAssignee?.name) }}
            </div>
            <div class="assignee-info">
              <span
                class="assignee-name"
                [title]="task.currentAssignee?.name || 'Unassigned'"
                >{{ task.currentAssignee?.name || "Unassigned" }}</span
              >
              <span
                class="assignee-email"
                [title]="task.currentAssignee?.email || ''"
                >{{ task.currentAssignee?.email }}</span
              >
            </div>
          </div>
        </div>
      </ng-template>
    </ng-container>

    <!-- Subtasks List -->
    <ng-container *ngIf="fields.fieldName === 'subTasks'">
      <!-- Loading Skeleton -->
      <ng-container *ngIf="isLoading; else subTaskData">
        <div *ngFor="let item of [1, 2, 3, 4]" class="reassign-card skeleton">
          <div class="col-span-5 flex items-center gap-3">
            <div class="skeleton-checkbox"></div>
            <div class="flex flex-col gap-2 w-full">
              <div class="skeleton-text w-32"></div>
              <div class="skeleton-text w-64"></div>
              <div class="flex gap-4">
                <div class="skeleton-text w-24"></div>
                <div class="skeleton-text w-24"></div>
              </div>
            </div>
          </div>
          <div class="col-span-4 flex items-center gap-3">
            <div class="skeleton-avatar"></div>
            <div class="flex flex-col gap-2">
              <div class="skeleton-text w-28"></div>
              <div class="skeleton-text w-36"></div>
            </div>
          </div>
          <div class="col-span-3 flex items-center gap-3">
            <div class="skeleton-arrow"></div>
            <div class="skeleton-avatar"></div>
            <div class="flex flex-col gap-2">
              <div class="skeleton-text w-28"></div>
              <div class="skeleton-text w-36"></div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Real Cards -->
      <ng-template #subTaskData>
        <div
          *ngFor="let task of visibleTasks; trackBy: trackByTask"
          [class.selected]="isTaskSelected(task)"
          class="reassign-card"
        >
          <!-- Checkbox + Subtask Details -->
          <div class="col-span-5 flex items-center gap-3">
            <input
              type="checkbox"
              class="checkbox text-blue-600 cursor-pointer"
              [id]="'option_' + task._id"
              [checked]="isTaskSelected(task)"
              (change)="toggleTaskSelection(task)"
            />
            <label [for]="'option_' + task._id" class="reassign-label">
              <span class="task-id-text" [title]="task.id">{{ task.id }}</span>
              <span class="task-desc-text" [title]="task.name">{{
                task.name
              }}</span>
              <div class="subtask-meta">
                <span class="meta-item"
                  ><span class="meta-label">Task ID:</span>
                  <span class="meta-val" [title]="task.taskId">{{
                    task.taskId
                  }}</span></span
                >
                <span class="meta-item"
                  ><span class="meta-label">Asset ID:</span>
                  <span class="meta-val" [title]="task.assetTag">{{
                    task.assetTag
                  }}</span></span
                >
              </div>
            </label>
          </div>

          <!-- Current Assignee -->
          <div class="col-span-4 flex items-center gap-3">
            <div
              [ngClass]="getBgColorByDepartment(task.previousAssignee?.name)"
              class="assignee-avatar"
            >
              {{ getInitials(task.previousAssignee?.name) }}
            </div>
            <div class="assignee-info">
              <span
                class="assignee-name"
                [title]="task.previousAssignee?.name || 'Unassigned'"
                >{{ task.previousAssignee?.name || "Unassigned" }}</span
              >
              <span
                class="assignee-email"
                [title]="task.previousAssignee?.email || ''"
                >{{ task.previousAssignee?.email }}</span
              >
            </div>
          </div>

          <!-- Reassign To -->
          <div class="col-span-3 flex items-center gap-3">
            <div class="reassign-arrow">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                />
              </svg>
            </div>
            <div
              [ngClass]="getBgColorByDepartment(task.currentAssignee?.name)"
              class="assignee-avatar"
            >
              {{ getInitials(task.currentAssignee?.name) }}
            </div>
            <div class="assignee-info">
              <span
                class="assignee-name"
                [title]="task.currentAssignee?.name || 'Unassigned'"
                >{{ task.currentAssignee?.name || "Unassigned" }}</span
              >
              <span
                class="assignee-email"
                [title]="task.currentAssignee?.email || ''"
                >{{ task.currentAssignee?.email }}</span
              >
            </div>
          </div>
        </div>
      </ng-template>
    </ng-container>
  </div>

  <!-- Sticky Footer -->
  <div
    class="reassign-footer"
    *ngIf="visibleTasks.length < filteredTasks.length"
  >
    <a (click)="viewMore()" class="reassign-footer-btn">
      <span>View More</span>
      <span class="footer-btn-count"
        >({{ visibleTasks.length }} of {{ filteredTasks.length }})</span
      >
    </a>

    <a
      *ngIf="
        visibleTasks.length >= filteredTasks.length &&
        filteredTasks.length > defaultVisibleCount
      "
      (click)="viewLess()"
      class="reassign-footer-btn"
    >
      View Less
    </a>
  </div>
</div>
